<template>
  <div class="settings-changelog-filters-bar">
    <div class="settings-changelog-filters-bar__row">
      <Select
        :model-value="employeeHrId"
        :options="employeeSelectOptions"
        optionLabel="label"
        optionValue="value"
        dataKey="value"
        placeholder="Search employee..."
        showClear
        filter
        autoFilterFocus
        resetFilterOnHide
        resetFilterOnClear
        filterPlaceholder="Type a name..."
        :filterFields="['label', 'department_name']"
        :loading="loadingEmployees"
        emptyMessage="No employees found"
        emptyFilterMessage="No employees found"
        class="settings-changelog-filters-bar__employee"
        overlayClass="settings-changelog-filters-overlay"
        :overlay-style="overlayStyle"
        appendTo="body"
        aria-label="Filter by employee"
        @update:model-value="onEmployeeChange"
        @filter="onEmployeeFilter"
        @show="onEmployeeSelectShow"
      >
        <template #value="slotProps">
          <EmployeeReportPersonRow
            v-if="resolveEmployeeOption(slotProps.value)"
            compact
            :person="resolveEmployeeOption(slotProps.value)"
            :show-department-tag="true"
            :link-to-profile="false"
          />
        </template>
        <template #option="{ option }">
          <EmployeeReportPersonRow
            compact
            :person="option"
            :show-department-tag="true"
            :link-to-profile="false"
          />
        </template>
      </Select>

      <div class="relative settings-changelog-filters-bar__search">
        <i class="pi pi-search settings-changelog-filters-bar__search-icon" aria-hidden="true" />
        <InputText
          :model-value="search"
          placeholder="Search field or actor..."
          class="w-full settings-changelog-filters-bar__search-input"
          aria-label="Search field or actor"
          @update:model-value="onSearchInput"
          @keyup.enter="emitApply"
        />
        <button
          v-if="search"
          type="button"
          class="settings-changelog-filters-bar__search-clear"
          aria-label="Clear search"
          @click="clearSearch"
        >
          <i class="pi pi-times" />
        </button>
      </div>
    </div>

    <div class="settings-changelog-filters-bar__row">
      <Select
        :model-value="action"
        :options="CHANGELOG_ACTION_OPTIONS"
        optionLabel="label"
        optionValue="value"
        placeholder="All actions"
        showClear
        class="settings-changelog-filters-bar__select"
        overlayClass="settings-changelog-filters-overlay"
        :overlay-style="overlayStyle"
        appendTo="body"
        @update:model-value="onActionChange"
      />

      <Select
        :model-value="entityType"
        :options="entityTypeOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="All sources"
        showClear
        class="settings-changelog-filters-bar__select settings-changelog-filters-bar__select--entity"
        overlayClass="settings-changelog-filters-overlay"
        :overlay-style="overlayStyle"
        appendTo="body"
        @update:model-value="onEntityTypeChange"
      />

      <DatePicker
        :model-value="dateFromModel"
        :date-format="PRIME_DATE_FORMAT_US"
        placeholder="From date"
        show-icon
        show-clear
        :manual-input="false"
        class="settings-changelog-filters-bar__date"
        panelClass="settings-changelog-filters-overlay"
        :panel-style="overlayStyle"
        :base-z-index="3000"
        appendTo="body"
        aria-label="From date"
        @update:model-value="onDateFromChange"
      />

      <DatePicker
        :model-value="dateToModel"
        :date-format="PRIME_DATE_FORMAT_US"
        placeholder="To date"
        show-icon
        show-clear
        :manual-input="false"
        class="settings-changelog-filters-bar__date"
        panelClass="settings-changelog-filters-overlay"
        :panel-style="overlayStyle"
        :base-z-index="3000"
        appendTo="body"
        aria-label="To date"
        @update:model-value="onDateToChange"
      />

      <Button
        label="Clear"
        size="small"
        severity="secondary"
        outlined
        :disabled="!hasFilters"
        @click="$emit('clear')"
      />
    </div>

    <div v-if="activeChips.length" class="settings-changelog-filters-bar__chips">
      <span
        v-for="chip in activeChips"
        :key="chip.key"
        class="settings-changelog-filters-bar__chip"
      >
        {{ chip.prefix }}: <strong>{{ chip.label }}</strong>
        <button
          type="button"
          class="settings-changelog-filters-bar__chip-remove"
          :aria-label="`Remove ${chip.prefix} filter`"
          @click="chip.onRemove"
        >
          <i class="pi pi-times" />
        </button>
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import EmployeeReportPersonRow from '@/components/employeeReports/EmployeeReportPersonRow.vue';
import { fetchEmployees } from '@/api/employeesApi';
import { isValidDateRange, toApiDate } from '@/constants/attendance';
import { PRIME_DATE_FORMAT_US } from '@/constants/dateFormats';
import { parseDateSafe } from '@/utils/dateHelper';
import {
  CHANGELOG_ACTION_OPTIONS,
  CHANGELOG_ENTITY_TYPE_OPTIONS,
  changelogEmployeeOptionFromRow,
  changelogEmployeePersonFromOption,
  changelogEntityTypeLabel,
  hasActiveChangelogFilters,
} from '../constants/changelogFilters';
import { auditActionLabel } from '@/utils/auditDisplayUtils';

const props = defineProps({
  search: { type: String, default: '' },
  action: { type: String, default: null },
  entityType: { type: String, default: null },
  dateFrom: { type: String, default: '' },
  dateTo: { type: String, default: '' },
  employeeHrId: { type: [Number, null], default: null },
  employeeLabel: { type: String, default: '' },
  employeeSnapshot: { type: Object, default: null },
  canViewCompensation: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:search',
  'update:action',
  'update:entityType',
  'update:dateFrom',
  'update:dateTo',
  'update:employeeHrId',
  'update:employeeLabel',
  'update:employeeSnapshot',
  'apply',
  'clear',
]);

const overlayStyle = { zIndex: 3000 };

const COMPENSATION_AUDIT_ENTITY_TYPES = ['employee_compensation', 'employee_compensation_item'];

const employeeOptions = ref([]);
const loadingEmployees = ref(false);
let searchTimer = null;
let employeeFilterTimer = null;

const entityTypeOptions = computed(() => {
  if (props.canViewCompensation) return CHANGELOG_ENTITY_TYPE_OPTIONS;
  return CHANGELOG_ENTITY_TYPE_OPTIONS.filter(
    (opt) => !COMPENSATION_AUDIT_ENTITY_TYPES.includes(opt.value)
  );
});

const dateFromModel = computed(() => parseDateSafe(props.dateFrom));
const dateToModel = computed(() => parseDateSafe(props.dateTo));

const hasFilters = computed(() =>
  hasActiveChangelogFilters({
    search: props.search,
    action: props.action,
    entity_type: props.entityType,
    date_from: props.dateFrom,
    date_to: props.dateTo,
    employee_hr_id: props.employeeHrId,
  })
);

const employeeSelectOptions = computed(() => {
  const list = employeeOptions.value.slice();
  const selectedId = Number(props.employeeHrId);
  if (!Number.isInteger(selectedId) || selectedId <= 0) return list;
  if (list.some((opt) => Number(opt.value) === selectedId)) return list;
  const snapshot = props.employeeSnapshot;
  if (snapshot && Number(snapshot.value) === selectedId) {
    return [{ ...snapshot, value: selectedId }, ...list];
  }
  const label = String(props.employeeLabel || '').trim() || `Employee #${selectedId}`;
  return [{
    value: selectedId,
    label,
    name: label,
    avatar_url: null,
    department_id: null,
    department_name: '',
    department_tag_color: null,
    department_tag_text_color: null,
  }, ...list];
});

const employeeOptionById = computed(() => {
  const map = new Map();
  employeeSelectOptions.value.forEach((opt) => {
    map.set(Number(opt.value), opt);
  });
  return map;
});

function resolveEmployeeOption(value) {
  const id = Number(value);
  if (!Number.isInteger(id) || id <= 0) return null;
  const option = employeeOptionById.value.get(id);
  return changelogEmployeePersonFromOption(option);
}

const activeChips = computed(() => {
  const chips = [];
  const selectedId = Number(props.employeeHrId);
  if (Number.isInteger(selectedId) && selectedId > 0) {
    const opt = employeeSelectOptions.value.find((item) => Number(item.value) === selectedId);
    chips.push({
      key: 'employee',
      prefix: 'Employee',
      label: opt?.label || props.employeeLabel || `#${selectedId}`,
      onRemove: clearEmployee,
    });
  }
  const q = String(props.search || '').trim();
  if (q) {
    chips.push({
      key: 'search',
      prefix: 'Search',
      label: q,
      onRemove: clearSearch,
    });
  }
  if (props.action) {
    chips.push({
      key: 'action',
      prefix: 'Action',
      label: auditActionLabel(props.action),
      onRemove: () => {
        emit('update:action', null);
        emitApply();
      },
    });
  }
  if (props.entityType) {
    chips.push({
      key: 'source',
      prefix: 'Source',
      label: changelogEntityTypeLabel(props.entityType),
      onRemove: () => {
        emit('update:entityType', null);
        emitApply();
      },
    });
  }
  if (props.dateFrom || props.dateTo) {
    const from = props.dateFrom || '…';
    const to = props.dateTo || '…';
    chips.push({
      key: 'dates',
      prefix: 'Dates',
      label: `${from} → ${to}`,
      onRemove: () => {
        emit('update:dateFrom', '');
        emit('update:dateTo', '');
        emitApply();
      },
    });
  }
  return chips;
});

function emitApply() {
  emit('apply');
}

function onSearchInput(value) {
  emit('update:search', value ?? '');
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(emitApply, 400);
}

function clearSearch() {
  emit('update:search', '');
  emitApply();
}

function onActionChange(value) {
  emit('update:action', value ?? null);
  emitApply();
}

function onEntityTypeChange(value) {
  emit('update:entityType', value ?? null);
  emitApply();
}

function onEmployeeChange(value) {
  const id = Number(value);
  const employeeHrId = Number.isInteger(id) && id > 0 ? id : null;
  emit('update:employeeHrId', employeeHrId);
  const opt = employeeSelectOptions.value.find((item) => Number(item.value) === employeeHrId);
  emit('update:employeeLabel', opt?.label || '');
  emit('update:employeeSnapshot', opt ? { ...opt } : null);
  emitApply();
}

function clearEmployee() {
  emit('update:employeeHrId', null);
  emit('update:employeeLabel', '');
  emit('update:employeeSnapshot', null);
  emitApply();
}

function onDateFromChange(value) {
  const from = toApiDate(value) || '';
  emit('update:dateFrom', from);
  if (!from || !props.dateTo || isValidDateRange(from, props.dateTo)) {
    emitApply();
  }
}

function onDateToChange(value) {
  const to = toApiDate(value) || '';
  emit('update:dateTo', to);
  if (!to || !props.dateFrom || isValidDateRange(props.dateFrom, to)) {
    emitApply();
  }
}

async function loadEmployees(search = '') {
  loadingEmployees.value = true;
  try {
    const data = await fetchEmployees({
      page: 1,
      limit: 80,
      search: String(search || '').trim(),
      is_active: 'all',
      roster_mode: 'all',
      sortField: 'name',
      sortOrder: 'ASC',
    });
    const next = [];
    const seen = new Set();
    (data.employees || []).forEach((row) => {
      const opt = changelogEmployeeOptionFromRow(row);
      if (!opt || seen.has(opt.value)) return;
      seen.add(opt.value);
      next.push(opt);
    });
    employeeOptions.value = next;
  } catch {
    employeeOptions.value = [];
  } finally {
    loadingEmployees.value = false;
  }
}

function onEmployeeFilter(event) {
  const query = event?.value ?? '';
  if (employeeFilterTimer) clearTimeout(employeeFilterTimer);
  employeeFilterTimer = setTimeout(() => {
    loadEmployees(query);
  }, 300);
}

function onEmployeeSelectShow() {
  if (!employeeOptions.value.length && !loadingEmployees.value) {
    loadEmployees('');
  }
}

onMounted(() => {
  loadEmployees('');
});

onUnmounted(() => {
  if (searchTimer) clearTimeout(searchTimer);
  if (employeeFilterTimer) clearTimeout(employeeFilterTimer);
});
</script>

<style scoped>
.settings-changelog-filters-bar {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
}

.settings-changelog-filters-bar__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.settings-changelog-filters-bar__employee {
  flex: 1 1 16rem;
  min-width: 14rem;
}

.settings-changelog-filters-bar__employee :deep(.p-select-label) {
  display: flex;
  align-items: center;
  min-height: 2.5rem;
  padding-top: 0.35rem;
  padding-bottom: 0.35rem;
}

.settings-changelog-filters-bar__employee :deep(.p-select-label > *) {
  min-width: 0;
  width: 100%;
}

.settings-changelog-filters-bar__search {
  flex: 1 1 14rem;
  min-width: 12rem;
}

.settings-changelog-filters-bar__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--crc-text-muted, var(--text-color-secondary));
  font-size: 0.875rem;
  pointer-events: none;
}

.settings-changelog-filters-bar__search-input {
  padding-left: 2.25rem;
  padding-right: 2rem;
  border-radius: 0.75rem;
}

.settings-changelog-filters-bar__search-clear {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--crc-text-muted, var(--text-color-secondary));
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  line-height: 1;
}

.settings-changelog-filters-bar__search-clear:hover {
  color: var(--crc-text, var(--text-color));
}

.settings-changelog-filters-bar__select {
  width: 10rem;
  min-width: 9rem;
}

.settings-changelog-filters-bar__select--entity {
  width: 11.5rem;
  min-width: 10rem;
}

.settings-changelog-filters-bar__date {
  width: 13rem;
  min-width: 12.25rem;
}

.settings-changelog-filters-bar__date :deep(.p-datepicker),
.settings-changelog-filters-bar__date :deep(.p-inputtext) {
  width: 100%;
}

.settings-changelog-filters-bar__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.settings-changelog-filters-bar__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  background: var(--crc-bar-bg, var(--surface-card));
  font-size: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
}

.settings-changelog-filters-bar__chip strong {
  color: var(--crc-text, var(--text-color));
  font-weight: 600;
}

.settings-changelog-filters-bar__chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--crc-text-muted, var(--text-color-secondary));
  cursor: pointer;
  font-size: 0.65rem;
}

.settings-changelog-filters-bar__chip-remove:hover {
  color: var(--crc-text, var(--text-color));
  background: color-mix(in srgb, var(--crc-text-muted, #9ca3af) 15%, transparent);
}
</style>

<style>
.settings-changelog-filters-overlay {
  z-index: 3000 !important;
}
</style>
